import './riepilogo-giustificativi.scss';

import React, { useEffect, useRef, useState } from 'react';
import { DataGrid, DateBox, LoadPanel, CheckBox, Button, SpeedDialAction } from 'devextreme-react';
import { Column, Toolbar, DataGridRef, Scrolling, Selection, DataGridTypes } from 'devextreme-react/data-grid';
import { Item } from "devextreme-react/toolbar";
import { useScreenSize, useScreenSizeClass } from '../../utils/media-query';
import useApiConfiguration from '../../apiConfig';
import { useUserSessionContext } from '../../contexts/UserSessionContext';
import { RiepGiustificativiPopupCaricaAllegati, RiepGiustificativiPopupCaricaArchivio, RiepGiustificativiPopupListaAllegati } from "../../components";
import { PopupCaricaAllegatiHandle } from '../../components/popup-riepilogo-giustificativi/popupCaricaAllegati';
import { PopupCaricaArchivioHandle } from "../../components/popup-riepilogo-giustificativi/popupCaricaArchivio";
import { PopupListaAllegatiHandle } from '../../components/popup-riepilogo-giustificativi/popupListaAllegati';
import { GiustificativiApi, GiustificativiResponseDTO, LavoriApi } from '../../data';
import config from "devextreme/core/config";

export default function RiepilogoGiustificativiPage() {
    const gridRef = useRef<DataGridRef>(null);
    const apiConfiguration = useApiConfiguration();
    const {user} = useUserSessionContext();
    const popupCaricaAllegatiRef = useRef<PopupCaricaAllegatiHandle>(null);
    const popupCaricaArchivioRef = useRef<PopupCaricaArchivioHandle>(null);
    const popupListaAllegatiRef = useRef<PopupListaAllegatiHandle>(null);

    const { isLarge, isMedium } = useScreenSize();
    const screenSizeClass = useScreenSizeClass();

    const [loading, setLoading] = useState<boolean>(false);
    const [personaleId, setPersonaleId] = useState(user.id);
    const [data, setData] = useState(() => {
        const today = new Date();
        return new Date(today.getFullYear(), today.getMonth(), 1);
    });
    const [onlyPending, setOnlyPending] = useState<boolean>(true);
    const [meseBloccato, setMeseBloccato] = useState<boolean>(false);
    const [canAddAllegato, setCanAddAllegato] = useState<boolean>(false);
    const [dataSource, setDataSource] = useState<any[]>([]);

    config({
        floatingActionButtonConfig: {
            icon: "add",
            shading: true,
            position: {
                my: "right bottom",
                at: "right bottom",
                of: "#gridRiepilogoGiustificativi",
                offset: "-16 -16"
            }
        }
    });

    function refreshData() {
        const api = new GiustificativiApi(apiConfiguration);
        const lavoriApi = new LavoriApi(apiConfiguration);
        const dataFiltro = new Date(data.getFullYear(), data.getMonth(), 1);

        lavoriApi.getBloccoCaricamentoLavori({data: dataFiltro, idPersonale: personaleId}).then((response) => {
            setMeseBloccato(!response.allowEdit);
        });

        api.getGiustificativi({
            mese: dataFiltro,
            hasAllegati: !onlyPending
        }).then((response) => {
            setDataSource(response);
        }).catch((error) => {
            console.error(error);
        }).finally(() => setLoading(false));
    };

    function onSelectionChanged(e: DataGridTypes.SelectionChangedEvent) {
        setCanAddAllegato(e.selectedRowsData.length > 0);
    };

    const columnAllegatiRender = (cell: DataGridTypes.ColumnCellTemplateData) => {
        const idLavoro = cell.data.idLavoro;
        const idTipoRimborso = cell.data.idTipoRimborso;
        const titolo = `${cell.data.commessa} - ${cell.data.tipo}`;

        if (!cell.data.hasAllegati)
            return null;

        return (
            <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left', gap: '10px', width: '100%' }}>
                <Button text="" stylingMode='text' hint="Vedi allegati" onClick={() => showAllegati(idLavoro, idTipoRimborso, titolo)}>
                    <i className='dx-icon material-symbols-outlined'>note_stack</i>
                </Button>
            </div>
        );
    };

    function caricaAllegati() {
        const dati = gridRef.current?.instance().getSelectedRowsData() as GiustificativiResponseDTO[];
        popupCaricaAllegatiRef.current?.show(dati);
    };

    function caricaArchivio() {
        const dati = gridRef.current?.instance().getSelectedRowsData() as GiustificativiResponseDTO[];
        popupCaricaArchivioRef.current?.show(dati);
    };
    
    function showAllegati(idLavoro: number, idTipoRimborso: number, titolo: string) {
        popupListaAllegatiRef.current?.show(idLavoro, idTipoRimborso, titolo);
    };

    useEffect(() => {
        setLoading(true);
        refreshData();
    }, [data, onlyPending]);

    return (
        <React.Fragment>
            {
                loading && <LoadPanel style={{ zIndex: 9999 }} shading={true} shadingColor='rgba(0, 0, 0, 0.4)' position={'center'} visible={true} />
            }

            <div className='view-list-giustificativi-riep'>
                <div className={`view-wrapper view-wrapper-list list-page ${screenSizeClass}`}>
                    <DataGrid
                        id="gridRiepilogoGiustificativi" ref={gridRef} dataSource={dataSource}
                        showBorders rowAlternationEnabled wordWrapEnabled columnAutoWidth 
                        onSelectionChanged={onSelectionChanged} className='grid theme-dependent'
                    >
                        <Scrolling mode='virtual' />
                        {
                            !meseBloccato && <Selection mode='multiple' selectAllMode='allPages' showCheckBoxesMode='always' />
                        }

                        <Toolbar>
                            <Item location='before'>
                                <div className='grid-header'>Giustificativi</div>
                            </Item>

                            <Item location='center' locateInMenu='never' showText='inMenu' widget='dxDateBox'>
                                <DateBox
                                    className={`dataSearch ${screenSizeClass}`}
                                    label="Mese" labelMode='floating'
                                    showClearButton={false}
                                    type="date" value={data}
                                    onValueChanged={(e) => setData(e.value)}
                                    displayFormat={ (isLarge || isMedium) ? "MMMM yyyy" : "MM/yyyy" }
                                    inputAttr={{ readonly: true }}
                                    openOnFieldClick
                                    calendarOptions={{
                                        maxZoomLevel: 'year',
                                        minZoomLevel: 'century'
                                    }}
                                />
                            </Item>

                            <Item location='center' locateInMenu='auto' showText='inMenu'>
                                <CheckBox value={onlyPending} onValueChanged={(e) => setOnlyPending(e.value)} text='Solo da caricare' />
                            </Item>
                        </Toolbar>

                        <Column dataField='idLavoro' visible={false} />
                        <Column dataField='idTipoRimborso' visible={false} />
                        <Column dataField='data' dataType='date' hidingPriority={2} />
                        <Column dataField='commessa' hidingPriority={3} />
                        <Column dataField='tipo' />
                        <Column dataField='localita' hidingPriority={1} />
                        <Column dataField='note' hidingPriority={0} />
                        <Column dataField='valore' />
                        <Column caption='Allegati' cellRender={columnAllegatiRender} />
                    </DataGrid>

                    <SpeedDialAction icon="import" label="Carica Allegato" visible={canAddAllegato && !meseBloccato} index={1} onClick={caricaAllegati} />
                    <SpeedDialAction icon="folder" label="Carica da Archivio" visible={canAddAllegato && !meseBloccato} index={2} onClick={caricaArchivio} />
                </div>
            </div>

            <RiepGiustificativiPopupCaricaAllegati ref={popupCaricaAllegatiRef} onHide={refreshData} />
            <RiepGiustificativiPopupCaricaArchivio ref={popupCaricaArchivioRef} onHide={refreshData} />
            <RiepGiustificativiPopupListaAllegati ref={popupListaAllegatiRef} />
        </React.Fragment>
    );
}